import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import type { Destination } from "../lib/types";
import { pickWhyBullets } from "../lib/matcher";
import { FeedbackModal } from "./FeedbackModal";
import { ShareCardModal } from "./ShareCardModal";

interface Props {
  destination: Destination;
  profile: ReturnType<typeof import("../lib/matcher").buildProfile>;
  sessionId: string;
  onReroll: () => void;
  onRestart: () => void;
}

export function Result({ destination, profile, sessionId, onReroll, onRestart }: Props) {
  const why = pickWhyBullets(destination, profile);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const chips: { cls: string; label: string }[] = [];
  destination.vibe_tags.slice(0, 2).forEach((t) => chips.push({ cls: "sea", label: vibeLabel(t) }));
  if (destination.chinese_friendly === "FRIENDLY_HIGH") chips.push({ cls: "success", label: "中文友好" });
  else if (destination.english_required === "EN_FLUENT") chips.push({ cls: "warn", label: "需要英语" });

  return (
    <section className="screen">
      <div
        className="result-hero"
        style={{ backgroundImage: `url(${destination.hero_photo})` }}
      >
        <QROverlay dest={destination.id} sid={sessionId} />
      </div>
      <div className="result-stack">
        <div className="result-eyebrow">你的下一片海是</div>
        <h1 className="result-name-zh">{destination.name_zh}</h1>
        <div className="result-name-en">{destination.name_en.toUpperCase()}</div>
        <div className="result-chips">
          {chips.map((c, i) => (
            <span key={i} className={"chip " + c.cls}>{c.label}</span>
          ))}
        </div>

        <div className="result-why">为什么是这里</div>
        <div className="result-why-list">
          {why.map((w, i) => (
            <div key={i} className="result-why-item">{w}</div>
          ))}
        </div>

        <div className="result-facts">
          <div className="result-fact"><div className="label">最佳季节</div><div className="value">{destination.season_zh}</div></div>
          <div className="result-fact"><div className="label">适合等级</div><div className="value">{destination.level_zh}</div></div>
          <div className="result-fact"><div className="label">预计天数</div><div className="value">{destination.days_zh}</div></div>
          <div className="result-fact"><div className="label">人均预算</div><div className="value">{destination.budget_zh}</div></div>
        </div>

        <div className="result-actions">
          <button className="btn btn-primary" onClick={() => setShowShare(true)}>保存图片 / 分享</button>
          <div className="row">
            <button className="btn btn-ghost" onClick={onReroll}>去过了</button>
            <button className="btn btn-ghost" onClick={onReroll}>不喜欢</button>
          </div>
          <button className="btn btn-text" onClick={onRestart}>再测一次</button>
        </div>

        <button className="feedback-link" onClick={() => setShowFeedback(true)}>
          反馈 · 觉得推荐不准？
        </button>
      </div>

      {showFeedback && (
        <FeedbackModal
          sessionId={sessionId}
          resultDestination={destination.id}
          onClose={() => setShowFeedback(false)}
        />
      )}

      {showShare && (
        <ShareCardModal
          destination={destination}
          sessionId={sessionId}
          onClose={() => setShowShare(false)}
        />
      )}
    </section>
  );
}

function QROverlay({ dest, sid }: { dest: string; sid: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    if (!canvasRef.current) return;
    const url = `${window.location.origin}/?ref=qr&dest=${dest}&sid=${sid}`;
    QRCode.toCanvas(canvasRef.current, url, {
      width: 56,
      margin: 1,
      color: { dark: "#000000", light: "#ffffff" },
    });
  }, [dest, sid]);
  return (
    <div className="result-qr-overlay">
      <canvas ref={canvasRef} />
    </div>
  );
}

function vibeLabel(tag: string): string {
  const map: Record<string, string> = {
    BIG_ANIMALS: "大物", MACRO: "微距", WRECK: "沉船", CAVE: "洞穴",
    DRIFT: "drift", REEF: "礁石", LIVEABOARD: "船宿", RESORT: "度假",
    CULTURE: "文化", NIGHTLIFE: "夜生活", REMOTE: "偏远", NATURE: "自然", FIRST_DIVE: "新手友好",
  };
  return map[tag] ?? tag;
}
